import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { formatDate, formatDateTime } from "@/lib/utils";
import {
  Edit,
  Shield,
  ShieldOff,
  UserCog,
  ExternalLink,
  AlertTriangle,
} from "lucide-react";
import type { UserActivity } from "@/types/admin";
import { UserDetailCard } from "@/components/users";
import { LoadingSpinner } from "@/components/common/LoadingSpinner";
import { ErrorFallback } from "@/components/common/ErrorFallback";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { SuspendUserDialog } from "@/components/users/SuspendUserDialog";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/page-header";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { auditActionVariant, formatAuditAction } from "@/lib/auditActions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useUserDetail,
  useSuspendUser,
  useActivateUser,
  useImpersonateUser,
  useUserAuditLogs,
  useUserTenants,
} from "@/hooks/useUserQueries";
import { EntityOrdersTab } from "@/components/orders/EntityOrdersTab";

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: user,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useUserDetail(id!);

  const { data: tenantsData, isLoading: isLoadingTenants } = useUserTenants(
    id!,
  );

  const [auditLogPage, setAuditLogPage] = useState(1);
  const AUDIT_LOG_LIMIT = 20;

  const {
    data: auditLogsResult,
    isLoading: isLoadingAuditLogs,
    isFetching: isFetchingAuditLogs,
    error: auditLogsError,
    refetch: refetchAuditLogs,
  } = useUserAuditLogs(id!, auditLogPage, AUDIT_LOG_LIMIT);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    // Names which mutation is behind the dialog, so its `isPending` can be read off the right
    // hook. Suspend has its own (SuspendUserDialog), because it asks for a reason.
    kind: "impersonate" | null;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    kind: null,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  const suspendUser = useSuspendUser();
  const activateUser = useActivateUser();
  const impersonateUser = useImpersonateUser();
  // Suspend has a dialog of its own: it asks for the reason, which ConfirmDialog cannot.
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  // Impersonation redirects with `window.location.href`, which does not resolve the mutation —
  // it just navigates the tab away. `isPending` alone would drop back to false the instant the
  // API answers, popping the overlay right before the browser actually leaves this page.
  const [isLeavingForImpersonation, setIsLeavingForImpersonation] =
    useState(false);
  const impersonationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const { hasRole } = usePermissions();
  const canManageUser = hasRole("SuperAdmin");

  // Two ways the redirect can fail to happen, both of which would otherwise leave the overlay up
  // forever: `event.persisted` fires when the browser restores this exact page from bfcache — the
  // case the flag was never designed for, since it assumes the navigate always goes through and
  // this page never comes back — and the timer covers anything else that keeps the response from
  // ever reaching `window.location.href` (a thrown redirect URL, a popup blocker, and so on).
  useEffect(() => {
    if (!isLeavingForImpersonation) {
      return;
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // A bfcache restore here means the impersonation already SUCCEEDED and the operator hit
        // Back — the client portal opened, this admin tab was merely parked. Claiming "the
        // redirect did not happen" is not just wrong, it invites a second impersonation, which
        // means another session and another audit row. Reset silently.
        setIsLeavingForImpersonation(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    // 20s, not 10s: a slow cross-origin portal load must not re-enable Impersonate mid-navigation
    // while the tab is still on its way out. Past this cap something genuinely did not go
    // through, so it is safe to invite a retry — but that is a real "nothing opened" case, not a
    // failure, so the message stays neutral rather than asserting a redirect that may still land.
    impersonationTimeoutRef.current = setTimeout(() => {
      setIsLeavingForImpersonation(false);
      toast.info(
        "Still waiting for the client portal — you can try again if nothing opened.",
      );
    }, 20_000);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      if (impersonationTimeoutRef.current) {
        clearTimeout(impersonationTimeoutRef.current);
        impersonationTimeoutRef.current = null;
      }
    };
  }, [isLeavingForImpersonation]);

  const isImpersonatePending =
    impersonateUser.isPending || isLeavingForImpersonation;
  // The overlay is reserved for writes that must not interleave with anything else on the page —
  // money, status, suspension, impersonate.
  const anyWritePending =
    suspendUser.isPending || activateUser.isPending || isImpersonatePending;
  const anyActionPending = anyWritePending;

  const confirmIsPending =
    confirmDialog.kind === "impersonate" ? isImpersonatePending : false;

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Full-screen only when there is nothing to show at all. A failed background refetch — after a
  // suspend/activate invalidation, say — used to wipe a user that was still sitting in the cache;
  // with one in hand the failure becomes a banner below instead of replacing the whole page.
  if (!user) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="User Details"
          subtitle="View and manage user information"
        />
        <ErrorFallback error={error as Error} onRetry={() => refetch()} />
      </div>
    );
  }

  const isActive = user.accountStatus === "Active";
  const userTenants = tenantsData ?? user.tenants ?? [];
  const auditLogs: UserActivity[] = auditLogsResult?.data ?? [];
  const auditLogPagination = auditLogsResult?.pagination;

  const handleSuspend = () => {
    setSuspendDialogOpen(true);
  };

  const handleConfirmSuspend = (reason: string) => {
    suspendUser.mutate(
      { id: user.id, reason },
      {
        // Closed on success only: on failure the dialog stays open with the reason the operator
        // typed, so a retry does not mean writing it again. SuspendUserDialog blocks every way of
        // closing it while the request runs, and re-enables Cancel once it fails.
        onSuccess: () => setSuspendDialogOpen(false),
      },
    );
  };

  const handleActivate = () => {
    // Fires straight from the header — there is nothing to confirm and no dialog to hold open, so
    // the header action's own `pending` spinner is this write's only sign of life.
    activateUser.mutate({ id: user.id, notes: "" }); // Notes should come from dialog input
  };

  const handleImpersonate = () => {
    setConfirmDialog({
      open: true,
      kind: "impersonate",
      title: "Impersonate User",
      description: `You will be logged in as ${user.fullName}. Continue?`,
      onConfirm: () => {
        impersonateUser.mutate(
          {
            id: user.id,
            reason: "Impersonation from admin panel",
          },
          {
            onSuccess: () => {
              // The redirect below is a location change, not something this mutation resolves —
              // keep the overlay up (and the dialog closed) until the browser actually navigates.
              setIsLeavingForImpersonation(true);
              setConfirmDialog((prev) => ({ ...prev, open: false }));
            },
            // On error there is no navigation coming, so nothing keeps the dialog open on
            // purpose — close it here (not onSettled) so a success doesn't also run this branch
            // and stack a redundant close on top of the one above.
            onError: () => {
              setConfirmDialog((prev) => ({ ...prev, open: false }));
            },
          },
        );
      },
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Details"
        subtitle="View and manage user information"
        // Every header action here writes to the user, and all four endpoints are SuperAdmin-only.
        // While one write is out, the rest are disabled — Activate is the only one that fires
        // directly from here, so it is also the only one that shows its own `pending` spinner;
        // the other three report through the ConfirmDialog/BusyOverlay they open instead.
        actions={
          !canManageUser
            ? []
            : [
                {
                  label: "Edit",
                  onClick: () => navigate(`/admin/users/${user.id}/edit`),
                  icon: <Edit className="mr-2 h-4 w-4" />,
                  disabled: anyActionPending,
                },
                {
                  label: "Impersonate",
                  onClick: handleImpersonate,
                  icon: <UserCog className="mr-2 h-4 w-4" />,
                  disabled: anyActionPending,
                },
                {
                  label: isActive ? "Suspend" : "Activate",
                  onClick: isActive ? handleSuspend : handleActivate,
                  icon: isActive ? (
                    <ShieldOff className="mr-2 h-4 w-4" />
                  ) : (
                    <Shield className="mr-2 h-4 w-4" />
                  ),
                  disabled: anyActionPending,
                  pending: !isActive && activateUser.isPending,
                },
              ]
        }
      />

      {/* The user is in hand; a failed background refetch — most often the invalidation that
          follows a suspend/activate/reset — is reported here instead of replacing the page. */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Could not refresh user details</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>The page may be showing a stale copy of this user.</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - User Info */}
        <div className="space-y-6 lg:col-span-1">
          <UserDetailCard user={user} />
        </div>

        {/* Right Column - Tabs */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="tenants" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="tenants">Tenant Memberships</TabsTrigger>
              <TabsTrigger value="orders">Orders</TabsTrigger>
              <TabsTrigger value="activity">Activity Logs</TabsTrigger>
            </TabsList>

            {/* Tenant Memberships Tab */}
            <TabsContent value="tenants">
              <Card>
                <CardHeader>
                  <CardTitle>
                    Tenant Memberships ({userTenants.length})
                  </CardTitle>
                  <CardDescription>
                    Organizations this user belongs to
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {isLoadingTenants && userTenants.length === 0 ? (
                    <div className="space-y-3 p-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : userTenants && userTenants.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tenant Name</TableHead>
                            <TableHead>Role</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Joined</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {userTenants.map((tenant) => (
                            <TableRow key={tenant.tenantId}>
                              <TableCell className="font-medium">
                                {tenant.tenantName}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">{tenant.role}</Badge>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={
                                    tenant.status === "Active"
                                      ? "default"
                                      : "destructive"
                                  }
                                >
                                  {tenant.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {formatDate(tenant.joinedAt)}
                              </TableCell>
                              <TableCell>
                                <Link to={`/admin/tenants/${tenant.tenantId}`}>
                                  <Button variant="ghost" size="sm">
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      No tenant memberships found
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="orders">
              <EntityOrdersTab userId={user.id} />
            </TabsContent>

            {/* Activity Logs Tab */}
            <TabsContent value="activity">
              <Card>
                <CardHeader>
                  <CardTitle>
                    Activity Logs (
                    {auditLogPagination?.totalItems ?? auditLogs.length})
                  </CardTitle>
                  <CardDescription>
                    Audit trail of actions performed by this user across the
                    platform
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {auditLogsError && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Failed to load activity log</AlertTitle>
                      <AlertDescription className="mt-2 flex items-center justify-between">
                        <span>
                          {auditLogsError instanceof Error
                            ? auditLogsError.message
                            : "An unexpected error occurred."}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => void refetchAuditLogs()}
                          disabled={isFetchingAuditLogs}
                        >
                          Retry
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}

                  {isLoadingAuditLogs && auditLogs.length === 0 ? (
                    <div className="space-y-3 p-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : auditLogs.length > 0 ? (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[180px]">Action</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead className="w-[140px]">
                              IP Address
                            </TableHead>
                            <TableHead className="w-[180px]">
                              Timestamp
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditLogs.map((entry) => {
                            const actionName =
                              entry.action || entry.actionType || "";
                            const occurredTime =
                              entry.occurredAt || entry.createdAt;

                            return (
                              <TableRow key={entry.id}>
                                <TableCell>
                                  <Badge
                                    variant={auditActionVariant(actionName)}
                                  >
                                    {formatAuditAction(actionName)}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-medium">
                                  <span>{entry.description || "—"}</span>
                                </TableCell>
                                <TableCell className="font-mono text-xs text-muted-foreground">
                                  {entry.ipAddress || "—"}
                                </TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                                  {formatDateTime(occurredTime)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="py-8 text-center text-muted-foreground">
                      No activity logs found
                    </div>
                  )}

                  {auditLogs.length > 0 &&
                    auditLogPagination &&
                    auditLogPagination.totalPages > 1 && (
                      <div className="mt-4 flex items-center justify-between border-t px-2 pt-4">
                        <div className="text-sm text-muted-foreground">
                          Page {auditLogPagination.currentPage} of{" "}
                          {auditLogPagination.totalPages} (
                          {auditLogPagination.totalItems} total)
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setAuditLogPage((p) => Math.max(1, p - 1))
                            }
                            disabled={
                              !auditLogPagination.hasPrev || isFetchingAuditLogs
                            }
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setAuditLogPage((p) => p + 1)}
                            disabled={
                              !auditLogPagination.hasNext || isFetchingAuditLogs
                            }
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          // Passing `isPending` already switches ConfirmDialog into async mode (Cancel and Escape
          // disabled), but guard the setter too: nothing here should ever drop the dialog while
          // its mutation is still in flight.
          if (open || !confirmIsPending) {
            setConfirmDialog((prev) => ({ ...prev, open }));
          }
        }}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        isPending={confirmIsPending}
      />

      <SuspendUserDialog
        open={suspendDialogOpen}
        onOpenChange={setSuspendDialogOpen}
        userName={user.fullName}
        isPending={suspendUser.isPending}
        onConfirm={handleConfirmSuspend}
      />

      <BusyOverlay
        open={anyWritePending}
        // Derived from whichever mutation is actually pending, not from `confirmDialog.kind` —
        // that field keeps its last value after the dialog closes, so a Suspend followed by an
        // Activate kept reading "Suspending user…" on the second write, which never opens a
        // dialog at all.
        label={
          activateUser.isPending
            ? "Activating user…"
            : suspendUser.isPending
              ? "Suspending user…"
              : isImpersonatePending
                ? "Starting impersonation…"
                : ""
        }
      />
    </div>
  );
}
